// NPC builds. The character goes through the normal avatar build (charged like
// any avatar, resumable by build id); the NPC primitive is placed straight
// away with status 'building' and filled in when the rigged GLB is ready.
// Whoever has the builder open (the account that paid) does the polling, and
// a reload picks the poll back up from npc.buildId.
import { startAvatarBuild, waitForAvatarBuild, AvatarBuildError } from './tripoAvatarBuild';
import { broadcastObjectUpdate } from './worldBroadcast';
import { useWorldStore } from '../stores/worldStore';
import type { NpcConfig } from '../config/npc';

export async function startNpcBuild(image: string, c: NpcConfig): Promise<string> {
  return startAvatarBuild({
    image,
    name: `NPC: ${c.name}`,
    remember: false,
    creationParams: { npc: 'true', gender: c.gender, ethnicity: c.ethnicity, type: c.type, clothing: c.clothing, style: c.style },
  });
}

function patchNpc(worldId: string, primId: string, patch: Partial<NpcConfig>) {
  const prim = useWorldStore.getState().primitives[primId];
  if (!prim?.npc) return; // deleted while building
  const npc = { ...prim.npc, ...patch };
  useWorldStore.getState().updatePrimitive(primId, { npc });
  broadcastObjectUpdate(worldId, 'primitive', primId, { npc });
}

const watching = new Set<string>();

/** Poll an NPC's build and fill in the avatar when it lands. Safe to call repeatedly. */
export function watchNpcBuild(worldId: string, primId: string, buildId: string) {
  if (watching.has(buildId)) return;
  watching.add(buildId);
  waitForAvatarBuild(buildId)
    .then((avatar) => patchNpc(worldId, primId, {
      status: 'ready', avatarUrl: avatar.model_url, avatarId: avatar.id,
      thumbnailUrl: avatar.thumbnail_url || undefined, buildId: undefined, error: undefined,
    }))
    .catch((err) => {
      // Someone else's build (a collaborator's NPC): not ours to poll or fail.
      if (err instanceof AvatarBuildError && /not found|Bad build/i.test(err.message)) return;
      patchNpc(worldId, primId, { status: 'failed', error: err?.message || 'Build failed' });
    })
    .finally(() => watching.delete(buildId));
}
